import {
  BookOpen,
  Bot,
  CheckCircle2,
  FileText,
  Gift,
  Globe,
  Link2,
  Repeat2,
  Share2,
  Star,
  Trophy,
  Users,
  Wallet,
} from 'lucide-react';

import { DiscordIcon, TelegramIcon, XLogoIcon } from '@/components/icons/BrandIcons';
import type { QuestIconName } from '@/lib/types';

interface QuestIconProps {
  name: QuestIconName;
  className?: string;
  /** Pixel size — passed straight to lucide. */
  size?: number;
  /**
   * Stroke width. Defaults to 1.75 to match the landing's hairline icon style
   * (Plus Jakarta + thin lucide pairs nicely at this weight). Ignored by the
   * brand marks (`x`, `message-circle`, `hash`) — those are solid fills, not
   * strokes.
   */
  strokeWidth?: number;
  'aria-hidden'?: boolean;
}

/**
 * Maps the curated `QuestIconName` union to a concrete icon element — either
 * a lucide-react icon or one of the real Discord/Telegram/X brand marks from
 * `BrandIcons`.
 *
 * Exhaustive switch — adding a new name to the union forces the `never`
 * assertion at the bottom to fail at compile time. Brand marks and lucide
 * icons have slightly different prop signatures (lucide's `strokeWidth` has
 * no equivalent on a solid brand mark), so this renders directly per-case
 * rather than handing back a single shared component reference.
 */
export function QuestIcon({
  name,
  className,
  size = 22,
  strokeWidth = 1.75,
  'aria-hidden': ariaHidden = true,
}: QuestIconProps) {
  switch (name) {
    case 'bigwater':
      return (
        <img
          src="/logomark.svg"
          alt=""
          width={size}
          height={size}
          className={className}
          aria-hidden={ariaHidden}
        />
      );
    case 'globe':
      return <Globe size={size} strokeWidth={strokeWidth} className={className} aria-hidden={ariaHidden} />;
    case 'book-open':
      return <BookOpen size={size} strokeWidth={strokeWidth} className={className} aria-hidden={ariaHidden} />;
    case 'file-text':
      return <FileText size={size} strokeWidth={strokeWidth} className={className} aria-hidden={ariaHidden} />;
    case 'x':
      return <XLogoIcon size={size} className={className} aria-hidden={ariaHidden} />;
    case 'message-circle':
      return <TelegramIcon size={size} className={className} aria-hidden={ariaHidden} />;
    case 'bot':
      return <Bot size={size} strokeWidth={strokeWidth} className={className} aria-hidden={ariaHidden} />;
    case 'users':
      return <Users size={size} strokeWidth={strokeWidth} className={className} aria-hidden={ariaHidden} />;
    case 'hash':
      return <DiscordIcon size={size} className={className} aria-hidden={ariaHidden} />;
    case 'share-2':
      return <Share2 size={size} strokeWidth={strokeWidth} className={className} aria-hidden={ariaHidden} />;
    case 'repeat-2':
      return <Repeat2 size={size} strokeWidth={strokeWidth} className={className} aria-hidden={ariaHidden} />;
    case 'star':
      return <Star size={size} strokeWidth={strokeWidth} className={className} aria-hidden={ariaHidden} />;
    case 'wallet':
      return <Wallet size={size} strokeWidth={strokeWidth} className={className} aria-hidden={ariaHidden} />;
    case 'link2':
      return <Link2 size={size} strokeWidth={strokeWidth} className={className} aria-hidden={ariaHidden} />;
    case 'trophy':
      return <Trophy size={size} strokeWidth={strokeWidth} className={className} aria-hidden={ariaHidden} />;
    case 'gift':
      return <Gift size={size} strokeWidth={strokeWidth} className={className} aria-hidden={ariaHidden} />;
    case 'check-circle':
      return <CheckCircle2 size={size} strokeWidth={strokeWidth} className={className} aria-hidden={ariaHidden} />;
    default: {
      const _exhaustive: never = name;
      return _exhaustive;
    }
  }
}

export default QuestIcon;
